import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { Card, Row, Col, ProgressBar, Container } from "react-bootstrap";
import { fetchMensajesEnviadosDesdeUltimoPago } from "../../services/firebase/resumenMensajes/index";
import { obtenerDatosUsuarios } from "../../services/firebase/perfilesUsuarios/index";
import { getAllPacientesCountByEmpresa } from "../../services/firebase/pacientes/index";
import { getPlanes } from "../../services/firebase/planes/index";
import { OBJECTIVES, VARIABLES_POLITICA } from "../../constants/Gamification";
import { isMembresiaSuspendida } from "../../helpers/membresia";

import PersonAdd from "@material-ui/icons/PersonAdd";
import LocalHospital from "@material-ui/icons/LocalHospital";
import People from "@material-ui/icons/People";
import Forum from "@material-ui/icons/Forum";
import HowToReg from "@material-ui/icons/HowToReg";
import MonetizationOn from "@material-ui/icons/MonetizationOn";
import Schedule from "@material-ui/icons/Schedule";
import CalendarToday from "@material-ui/icons/CalendarToday";

const getPlanLimits = (empresa, planesList = []) => {
  const dbMembresia = empresa.membresia || {};

  let planName = "FREE";
  if (dbMembresia.plan) {
    if (typeof dbMembresia.plan === "object") {
      planName = (
        dbMembresia.plan.nombre ||
        dbMembresia.plan.plan ||
        "FREE"
      ).toUpperCase();
    } else {
      planName = dbMembresia.plan.toUpperCase();
    }
  } else if (empresa.tipoMembresia) {
    planName = empresa.tipoMembresia.toUpperCase();
  }

  const dbPlan = planesList.find(
    (p) =>
      p.id.toLowerCase() === planName.toLowerCase() ||
      p.nombre?.toLowerCase() === planName.toLowerCase(),
  );

  const isPrepago = (empresa.tipoMembresia || "").toUpperCase() === "PREPAGO";
  if (isPrepago) {
    return {
      plan: "PREPAGO",
      usuarios: 1,
      pacientes: 50,
      maxMensajesWhatsapp: empresa.saldoMensajesWhatsapp || 0,
    };
  }

  const dbFreePlan =
    planesList.find(
      (p) =>
        p.id.toLowerCase() === "free" || p.nombre?.toLowerCase() === "free",
    ) || {};

  const limitSource =
    typeof dbMembresia.plan === "object" ? dbMembresia.plan : {};

  return {
    plan: planName,
    usuarios:
      dbMembresia.usuarios ||
      limitSource.limiteUsuarios ||
      limitSource.usuarios ||
      dbPlan?.limiteUsuarios ||
      dbPlan?.usuarios ||
      dbFreePlan.limiteUsuarios ||
      dbFreePlan.usuarios ||
      0,
    pacientes:
      dbMembresia.pacientes ||
      limitSource.limitePacientes ||
      limitSource.pacientes ||
      dbPlan?.limitePacientes ||
      dbPlan?.pacientes ||
      dbFreePlan.limitePacientes ||
      dbFreePlan.pacientes ||
      0,
    maxMensajesWhatsapp:
      dbMembresia.maxMensajesWhatsapp ||
      limitSource.maxMensajesWhatsapp ||
      dbPlan?.maxMensajesWhatsapp ||
      dbFreePlan.maxMensajesWhatsapp ||
      0,
  };
};

const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date =
    typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : new Date(timestamp);
  return moment(date).format("DD/MM/YYYY");
};

export const Inicio = () => {
  const sesion = useSelector((state) => state.sesion);
  const empresa = useSelector((state) => state.empresa);
  const [loading, setLoading] = useState(true);
  const [planes, setPlanes] = useState([]);
  const [counts, setCounts] = useState({
    pacientes: 0,
    prestaciones: 0,
    citas: 0,
    usuarios: 0,
    whatsapp: 0,
  });

  const limits = getPlanLimits(empresa, planes);

  useEffect(() => {
    async function fetchAllStats() {
      if (!empresa || !empresa.id) return;
      setLoading(true);
      try {
        const currentDate = new Date();
        const empresaId = empresa.id;

        const [pacientesCount, usuariosData, whatsappCount, planesList] =
          await Promise.all([
            getAllPacientesCountByEmpresa(empresaId),
            obtenerDatosUsuarios(empresaId),
            fetchMensajesEnviadosDesdeUltimoPago(empresaId, empresa.fecUltPago),
            getPlanes(),
          ]);

        setPlanes(planesList || []);
        setCounts({
          pacientes: pacientesCount || 0,
          usuarios: Array.isArray(usuariosData) ? usuariosData.length : 0,
          whatsapp: whatsappCount || 0,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAllStats();
  }, [empresa]);

  const renderLimitCard = (label, value, limit, icon) => {
    const limitVal = Number(limit) || 0;
    const currentVal = Number(value) || 0;
    const percent =
      limitVal > 0
        ? Math.min(100, Math.round((currentVal / limitVal) * 100))
        : 0;

    let barVariant = "success";
    if (percent >= 100) {
      barVariant = "danger";
    } else if (percent >= 90) {
      barVariant = "warning";
    }

    return (
      <Card
        className="mb-4 shadow-sm border-0 h-100"
        style={{ borderRadius: "12px" }}
      >
        <Card.Body className="d-flex flex-column justify-content-between p-4">
          <div className="d-flex align-items-center mb-3">
            <div
              className="p-3 bg-light rounded-circle me-3 d-flex align-items-center justify-content-center text-primary"
              style={{ width: "50px", height: "50px" }}
            >
              {icon}
            </div>
            <div>
              <h6
                className="text-muted mb-0 text-uppercase fw-bold"
                style={{ fontSize: "0.8rem", letterSpacing: "0.5px" }}
              >
                {label}
              </h6>
              <h3 className="mb-0 fw-bold" style={{ color: "#0C234C" }}>
                {currentVal}{" "}
                <span
                  className="text-muted fw-normal"
                  style={{ fontSize: "0.95rem" }}
                >
                  / {limitVal > 0 ? limitVal : "∞"}
                </span>
              </h3>
            </div>
          </div>
          <div>
            <div
              className="d-flex justify-content-between align-items-center mb-1"
              style={{ fontSize: "0.85rem" }}
            >
              <span className="text-muted">Uso</span>
              <span className="fw-bold">{percent}%</span>
            </div>
            <ProgressBar
              now={percent}
              variant={barVariant}
              style={{ height: "8px", borderRadius: "4px" }}
            />
          </div>
        </Card.Body>
      </Card>
    );
  };

  const PlanDetailsCard = ({ empresa, limits }) => {
    const isSuspended = isMembresiaSuspendida(empresa);
    const dbMembresia = empresa.membresia || {};
    const dbPlan =
      planes.find(
        (p) =>
          p.id.toLowerCase() === limits.plan.toLowerCase() ||
          p.nombre?.toLowerCase() === limits.plan.toLowerCase(),
      ) || {};
    console.log("Empresa:", empresa);
    console.log("Membresia:", dbMembresia);
    console.log("Plan Detalles:", dbPlan);
    const planDisplayName = (
      dbMembresia.plan?.id ||
      dbPlan?.id ||
      "FREE"
    ).toUpperCase();
    const isPrepago = (empresa.tipoMembresia || "").toUpperCase() === "PREPAGO";
    let badgeBg = "#28a745";
    let badgeText = "FREE";
    if (isPrepago) {
      badgeBg = "#10b981";
      badgeText = "PREPAGO ⚡";
    } else if (planDisplayName === "GOLD") {
      badgeBg = "#ffd700";
      badgeText = "GOLD (ORO)";
    } else if (planDisplayName === "SILVER") {
      badgeBg = "#c0c0c0";
      badgeText = "SILVER (PLATA)";
    } else if (planDisplayName === "BASIC") {
      badgeBg = "#0c8de3";
      badgeText = "BASIC";
    } else if (planDisplayName === "DEMO") {
      badgeBg = "#67D2DC";
      badgeText = "DEMO";
    }

    return (
      <Card
        className="mb-4 shadow-sm border-0 text-white"
        style={{
          borderRadius: "12px",
          background: "linear-gradient(135deg, #0C234C 0%, #0c8de3 100%)",
          padding: "24px",
        }}
      >
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h5
                className="text-white-50 text-uppercase fw-bold mb-1"
                style={{ fontSize: "0.8rem", letterSpacing: "0.5px" }}
              >
                {isPrepago ? "Modalidad Prepago" : "Plan Contratado"}
              </h5>
              <h2 className="fw-bold text-white mb-0">
                {empresa.razonSocial?.toUpperCase() || "Mi Consultorio"}
              </h2>
            </div>
            <span
              className="px-3 py-2 rounded-pill fw-bold"
              style={{
                backgroundColor: badgeBg,
                color: limits.plan === "GOLD" ? "#0C234C" : "#ffffff",
                fontSize: "0.85rem",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
              }}
            >
              {badgeText}
            </span>
          </div>

          <Row className="g-3">
            <Col xs={6} sm={3}>
              <div className="border-start border-2 border-info ps-3">
                <span
                  className="text-white-50 d-block"
                  style={{ fontSize: "0.8rem" }}
                >
                  Último Pago
                </span>
                <strong className="text-white" style={{ fontSize: "1.05rem" }}>
                  {formatFirestoreDate(empresa.fecUltPago)}
                </strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="border-start border-2 border-info ps-3">
                <span
                  className="text-white-50 d-block"
                  style={{ fontSize: "0.8rem" }}
                >
                  Vencimiento
                </span>
                <strong className="text-white" style={{ fontSize: "1.05rem" }}>
                  {formatFirestoreDate(empresa.fecVen)}
                </strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="border-start border-2 border-info ps-3">
                <span
                  className="text-white-50 d-block"
                  style={{ fontSize: "0.8rem" }}
                >
                  Saldo WhatsApp
                </span>
                <strong className="text-white" style={{ fontSize: "1.05rem" }}>
                  💬 {empresa.saldoMensajesWhatsapp !== undefined ? `${empresa.saldoMensajesWhatsapp} msgs` : (isPrepago ? "0 msgs" : `${limits.maxMensajesWhatsapp || 0} msgs`)}
                </strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="border-start border-2 border-info ps-3">
                <span
                  className="text-white-50 d-block"
                  style={{ fontSize: "0.8rem" }}
                >
                  Estado
                </span>
                <strong className="text-white" style={{ fontSize: "1.05rem" }}>
                  {isSuspended ? (
                    <span className="text-danger fw-bold">
                      SUSPENDIDO
                    </span>
                  ) : (
                    <span className="text-success fw-bold">ACTIVO</span>
                  )}
                </strong>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container className="py-5">
      <PlanDetailsCard empresa={empresa} limits={limits} />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando consumo del plan...</p>
        </div>
      ) : (
        <Row className="mt-4">
          <Col xs={12} md={6} lg={3} className="mb-4">
            {renderLimitCard(
              "Pacientes Registrados",
              counts.pacientes,
              limits.pacientes,
              <PersonAdd />,
            )}
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-4">
            {renderLimitCard(
              "Usuarios de Consultorio",
              counts.usuarios,
              limits.usuarios,
              <People />,
            )}
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-4">
            {renderLimitCard(
              "Consumo WhatsApp",
              counts.whatsapp,
              limits.maxMensajesWhatsapp,
              <Forum />,
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};
